import React, { useMemo, useState } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import styled from "styled-components";

const API_BASE_URL = "https://server229256.nazwa.pl";
const TERMS_URL = "/contact";
const PRIVACY_URL = "/contact";

const getDemoSession = () => {
  const token = localStorage.getItem("token") || "";

  try {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    const license = String(user?.LicenseType || user?.licenseType || "")
      .trim()
      .toUpperCase();
    if (license === "BASIC_DEMO") return { isBasicDemo: true, token };
  } catch {
    // Fall back to JWT.
  }

  try {
    const license = String(jwtDecode(token)?.LicenseType || "")
      .trim()
      .toUpperCase();
    return { isBasicDemo: license === "BASIC_DEMO", token };
  } catch {
    return { isBasicDemo: false, token };
  }
};

const BasicDemoConsentModal = () => {
  const session = useMemo(getDemoSession, []);
  const storageKey = session.token
    ? `basic-demo-consent:${session.token.slice(-24)}`
    : "basic-demo-consent";

  const [visible, setVisible] = useState(
    session.isBasicDemo && sessionStorage.getItem(storageKey) !== "accepted"
  );
  const [email, setEmail] = useState("");
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [rodoAccepted, setRodoAccepted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  if (!visible) return null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    const cleanEmail = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      setError("Podaj prawidłowy adres e-mail.");
      return;
    }
    if (!legalAccepted || !rodoAccepted) {
      setError("Zaakceptuj oba wymagane oświadczenia.");
      return;
    }

    setSaving(true);
    try {
      await axios.post(
        `${API_BASE_URL}/api/basic-demo-access`,
        { email: cleanEmail, legalAccepted, rodoAccepted },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: session.token,
          },
        }
      );

      sessionStorage.setItem(storageKey, "accepted");
      setVisible(false);
    } catch (requestError) {
      setError(
        requestError?.response?.data?.error ||
          "Nie udało się zapisać zgody. Spróbuj ponownie."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Overlay role="dialog" aria-modal="true" aria-labelledby="demo-consent-title">
      <Modal>
        <Badge>WERSJA BASIC DEMO</Badge>
        <h2 id="demo-consent-title">Dane demonstracyjne</h2>
        <p>
          Wszystkie dane prezentowane w tej wersji aplikacji służą wyłącznie
          celom demonstracyjnym.
        </p>
        <p>
          Aby kontynuować korzystanie z wersji demo, podaj swój adres e-mail i
          zaakceptuj wymagane oświadczenia.
        </p>

        <Form onSubmit={handleSubmit}>
          <label htmlFor="basic-demo-email">Adres e-mail</label>
          <EmailInput
            id="basic-demo-email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="twoj@email.pl"
            autoComplete="email"
            maxLength={254}
            required
            autoFocus
          />

          <CheckboxLabel>
            <input
              type="checkbox"
              checked={legalAccepted}
              onChange={(event) => setLegalAccepted(event.target.checked)}
              required
            />
            <span>
              Akceptuję <a href={TERMS_URL} target="_blank" rel="noreferrer">regulamin</a>{" "}
              korzystania z wersji demonstracyjnej.
            </span>
          </CheckboxLabel>

          <CheckboxLabel>
            <input
              type="checkbox"
              checked={rodoAccepted}
              onChange={(event) => setRodoAccepted(event.target.checked)}
              required
            />
            <span>
              Zapoznałem(-am) się z{" "}
              <a href={PRIVACY_URL} target="_blank" rel="noreferrer">informacją RODO</a>{" "}
              i akceptuję przetwarzanie danych w celu udostępnienia wersji demo.
            </span>
          </CheckboxLabel>

          {error && <ErrorMessage role="alert">{error}</ErrorMessage>}

          <ContinueButton type="submit" disabled={saving}>
            {saving ? "Zapisywanie…" : "Akceptuję i przechodzę do DEMO"}
          </ContinueButton>
        </Form>
      </Modal>
    </Overlay>
  );
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: grid;
  place-items: center;
  padding: 20px;
  background: rgba(15, 23, 42, 0.72);
  backdrop-filter: blur(8px);
`;

const Modal = styled.section`
  width: min(580px, 100%);
  max-height: calc(100vh - 40px);
  overflow-y: auto;
  padding: 34px;
  border-radius: 20px;
  background: #ffffff;
  box-shadow: 0 28px 90px rgba(0, 0, 0, 0.32);

  h2 { margin: 14px 0 12px; color: #172033; font-size: 2rem; }
  p { margin: 8px 0; color: #526071; line-height: 1.6; }
`;

const Badge = styled.span`
  display: inline-block;
  padding: 6px 11px;
  border-radius: 999px;
  background: #dff8fa;
  color: #11747b;
  font-size: 0.78rem;
  font-weight: 900;
  letter-spacing: 0.06em;
`;

const Form = styled.form`
  display: grid;
  gap: 15px;
  margin-top: 24px;

  > label:first-child { color: #172033; font-weight: 800; }
`;

const EmailInput = styled.input`
  width: 100%;
  padding: 13px 14px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  font: inherit;

  &:focus { outline: 3px solid rgba(59, 195, 204, 0.24); border-color: #3bc3cc; }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 11px;
  color: #465365;
  line-height: 1.45;

  input { width: 18px; height: 18px; margin-top: 2px; flex: 0 0 auto; }
  a { color: #11747b; font-weight: 800; }
`;

const ErrorMessage = styled.div`
  padding: 10px 12px;
  border-radius: 9px;
  background: #fee2e2;
  color: #991b1b;
  font-weight: 700;
`;

const ContinueButton = styled.button`
  min-height: 50px;
  border: 0;
  border-radius: 999px;
  background: #3bc3cc;
  color: #ffffff;
  font-size: 1rem;
  font-weight: 900;
  cursor: pointer;

  &:hover:not(:disabled) { background: #172033; }
  &:disabled { opacity: 0.65; cursor: wait; }
`;

export default BasicDemoConsentModal;
